import React from "react";
import { useLocation } from "react-router-dom";
import Avatar from "src/components/Avatar";
import Link from "src/components/Link";
import Skeleton from "src/components/Skeleton";
import useVariant, { VariantTypes } from "src/hooks/useVariant";

interface ListItemProps {
	children?: any;
	to?: string;
	className?: string;
	align: "start" | "center" | "end";
	bordered?: boolean;
	activeClassName?: string;
}

const ListItem = ({
	children,
	to,
	className: _className,
	align,
	bordered,
	activeClassName,
}: ListItemProps) => {
	const location = useLocation();
	let className = `flex items-${align} group gap-3 relative transition-all ${_className} ${
		to && "hover:opacity-90"
	}`;

	if (bordered) {
		className += ` border-b-[2px] border-gray pb-4`;
	}

	if (to) {
		const path = to?.split("#")[0];
		const isActive = location.pathname === path;
		return (
			<Link {...{ to, className, activeClassName }}>
				<div className="absolute right-0 top-0">
					<div
						className={`w-6 h-6 rounded-full flex justify-center transition-all group-hover:bg-primary group-hover:text-white items-center ${
							isActive ? "bg-primary text-white" : "bg-gray"
						}`}
					>
						<i className="fas fa-angle-right text-[12px]"></i>
					</div>
				</div>
				{children}
			</Link>
		);
	}

	return <div {...{ className }}>{children}</div>;
};

ListItem.defaultProps = {
	align: "center",
};

interface IconProps {
	name: string;
	variant: VariantTypes;
}

const Icon = ({ name, variant }: IconProps) => {
	const _variant = useVariant(variant);

	return (
		<div
			className={`w-12 h-12 rounded-md flex justify-center items-center ${_variant}`}
		>
			<i className={`${name}`}></i>
		</div>
	);
};

interface ContentProps {
	children?: any;
	className?: string;
}

const Content = ({ children, className }: ContentProps) => (
	<div className={`flex-1 flex flex-col ${className || ""}`}>{children}</div>
);

// const ListItemAvatar = (props: AvatarProps) => {
// 	return <Avatar {...props} />;
// };

interface TitleProps {
	children?: any;
	className?: string;
	loading?: boolean;
}

const Title = ({ children, className, loading }: TitleProps) => {
	if (loading) {
		return <Skeleton className={`h-4 w-1/4 mb-1`}></Skeleton>;
	}
	return <p className={`font-bold ${className}`}>{children}</p>;
};

interface TextProps {
	children?: any;
	className?: string;
	variant: "paragraph" | "small";
	loading?: boolean;
}

const Text = ({
	children,
	className: _className,
	variant,
	loading,
}: TextProps) => {
	const className = `${_className || ""}`;
	if (loading) {
		return (
			<Skeleton
				className={`${
					!className?.includes("w-") && "w-2/4"
				} ${className} ${variant === "small" ? "h-3" : "h-4"}`}
			/>
		);
	}
	if (variant === "small") {
		return <small {...{ className }}>{children}</small>;
	}
	return <p {...{ className }}>{children}</p>;
};

Text.defaultProps = {
	variant: "paragraph",
};

ListItem.Icon = Icon;
ListItem.Title = Title;
ListItem.Text = Text;
ListItem.Avatar = Avatar;
ListItem.Content = Content;

export default ListItem;
